import { useMutation } from "@apollo/client";
import { AnonymousTeamFilterDropdown } from "@components/teams/AnonymousTeamFilterDropdown";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import AutoTemplatesList from "./AutoTemplatesList";
import { createNewTemplate } from "./utils";
import {
  UPDATE_AUTO_TEMPLATE,
  CREATE_AUTO_TEMPLATE,
} from "../../graphql/mutations/AutoTemplates";
import {
  useAccount,
  useAuth,
  useTeam,
  usePremiumFeature,
  useNoteUsage,
} from "../../hooks";
import { alert } from "../common/Alert";
import { ConfirmModal } from "../common/modals/PopupModals";
import { SCRIBENOTE_DEFAULTS } from "../common/record/constants";
import { isUUID } from "../dashboard/utils";

const AutoTemplatesModal = ({
  isOpen,
  onClose,
  onTemplateSelect,
  templates,
  refetch,
  isReprocessingMode = false,
}) => {
  const [updateAutoTemplate] = useMutation(UPDATE_AUTO_TEMPLATE);
  const [createAutoTemplate] = useMutation(CREATE_AUTO_TEMPLATE);
  const navigate = useNavigate();
  const { account } = useAccount();
  const { enableAdaptiveTemplates, currentTeam } = useTeam();
  const { freeMode } = useFlags();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();
  const { hasReachedAutoSoapLimit } = useNoteUsage();

  // filtering
  const { userUuid } = useAuth();
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([
    userUuid,
  ]);
  // end filtering

  function canReprocessAsTemplate(selectedJobType) {
    const templateValue =
      selectedJobType?.value || selectedJobType?.uuid;
    if (account?.accountType === "unlimited") {
      return;
    }

    const warningJobTypes = [
      "medical_record",
      "packmode",
      "multipet",
    ];

    if (
      !warningJobTypes.includes(templateValue) &&
      !isUUID(templateValue)
    ) {
      return;
    }

    if (hasReachedAutoSoapLimit && freeMode) {
      return "limit";
    }
  }

  const handleFavouriteToggle = async (template) => {
    await updateAutoTemplate({
      variables: {
        input: {
          templateUuid: template?.uuid,
          isFavourite: !template?.isFavourite,
        },
      },
    });
    refetch();
  };

  if (!isOpen) return null;

  const templateLists = [
    {
      title: "SOAPs",
      templates,
      templateType: "SOAP",
    },
    {
      title: "Custom",
      templates,
      templateType: "CUSTOM",
    },
    {
      title: "Scribenote Defaults",
      templates: SCRIBENOTE_DEFAULTS,
      templateType: "SCRIBENOTE_DEFAULT",
    },
  ];

  const handleCreateNewTemplate = async () => {
    const templateUuid = await createNewTemplate(
      "SOAP",
      true,
      createAutoTemplate,
    );
    navigate(`/templates/edit/${templateUuid}?isNew=true`);
  };

  if (document.readyState === "complete") {
    return createPortal(
      <ConfirmModal
        shouldShow={isOpen}
        cancelAction={onClose}
        hideModal={onClose}
        confirmAction={handleCreateNewTemplate}
        hideConfirm={!enableAdaptiveTemplates}
        confirmText={"+ New Adaptive Template"}
        title={
          isReprocessingMode ? "Change Template" : "Select Template"
        }
        subtitle={
          <>
            {isReprocessingMode
              ? "Select an option below to apply this template to the note. This will overwrite any changes you have made to the existing note."
              : "Select an option below to create a new note using the selected template."}
            {currentTeam?.uuid && (
              <div className="flex mt-1">
                <AnonymousTeamFilterDropdown
                  selectedTeamMembers={selectedTeamMembers}
                  setSelectedTeamMembers={setSelectedTeamMembers}
                />
              </div>
            )}
          </>
        }
        width="max-w-[1200px]"
        height="h-[60vh] max-h-[600px]"
        lockConfirm={
          !hasFeatureAccess(
            UserPermissions.ADAPTIVE_TEMPLATES_ENABLED,
          )
        }
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 pt-0 min-h-96">
          <div className="flex flex-col gap-4 px-4">
            {templateLists.map(
              ({ title, templates, templateType }) => (
                <AutoTemplatesList
                  key={templateType}
                  title={title}
                  templates={templates.filter(
                    (i) =>
                      !i?.user?.uuid ||
                      selectedTeamMembers.includes(i.user.uuid),
                  )}
                  templateType={templateType}
                  onFavouriteToggle={handleFavouriteToggle}
                  onTemplateClick={(template) => {
                    const canReprocess =
                      canReprocessAsTemplate(template);
                    if (canReprocess === "limit") {
                      alert(
                        "error",
                        "This template is only available for paid plans. Please upgrade to use this template.",
                      );
                    } else {
                      onTemplateSelect(template);
                      onClose();
                    }
                  }}
                  isModal={true}
                />
              ),
            )}
          </div>
        </div>
      </ConfirmModal>,
      document.getElementById("root-node"),
    );
  }
};

export default AutoTemplatesModal;
